import Component from '@glimmer/component';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import UiCard from 'garaje/components/ui/card';

function formatRelativeTime(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
}

interface Integration {
  successRate?: number | null;
  successRate24h?: number | null;
  lastSeenAt?: string | null;
  activeOperationTypes?: string[];
  totalOperations?: number;
  totalEvents?: number;
}

interface StatCard {
  label: string;
  value: string;
  rangedValue: string;
  trend: string | null;
  deltaDisplay: string | null;
  deltaPositive: boolean | null;
  deltaTooltip: string;
  rangeLabel: string;
}

interface HealthResponse {
  integrations: Integration[];
  operationsSent?: number;
  operationsReceived?: number;
  accessEventsReceived?: number;
}

interface StatsCardsSignature {
  Args: {
    previousRangeIntegrations: Integration[];
    currentRangeIntegrations: Integration[];
    previousRangeHealth?: HealthResponse;
    currentRangeHealth?: HealthResponse;
    selectedTimeRange: string;
  };
}

export default class StatsCards extends Component<StatsCardsSignature> {
  get stats(): StatCard[] {
    const currentRangeIntegrations = this.args.currentRangeIntegrations ?? [];
    const previousRangeIntegrations = this.args.previousRangeIntegrations ?? [];
    const { selectedTimeRange } = this.args;

    const deltaTooltip = `${selectedTimeRange} vs previous ${selectedTimeRange}`;

    const currentRange = currentRangeIntegrations;
    const previousRange = previousRangeIntegrations;

    const pctChange = (currentVal: number, previousVal: number) =>
      previousVal !== 0 ? `${((Math.abs(currentVal - previousVal) / Math.abs(previousVal)) * 100).toFixed(1)}%` : null;

    const makeStat = (
      label: string,
      currentVal: number | null,
      previousVal: number | null,
      formatVal: (v: number) => string,
      showDelta = true,
    ): StatCard => {
      let trend = null;
      let deltaDisplay = null;
      let deltaPositive = null;
      if (currentVal != null && previousVal != null) {
        if (currentVal > previousVal) trend = 'higher';
        else if (currentVal < previousVal) trend = 'lower';
        else trend = 'same';
        deltaDisplay = showDelta ? pctChange(currentVal, previousVal) : null;
        deltaPositive = currentVal >= previousVal;
      }
      return {
        label,
        value: previousVal != null ? formatVal(previousVal) : '—',
        rangedValue: currentVal != null ? formatVal(currentVal) : '—',
        trend,
        deltaDisplay,
        deltaPositive,
        deltaTooltip,
        rangeLabel: selectedTimeRange,
      };
    };

    // Integration-derived cards (require per-install data)
    const integrationStats: StatCard[] = [];
    if (currentRange.length > 0) {
      const avgSuccessRate = (list: Integration[]) =>
        list.length > 0 ? list.reduce((s, i) => s + (i.successRate ?? i.successRate24h ?? 0), 0) / list.length : null;

      const latestMs = (list: Integration[]) => {
        const ts = list.map((i) => i.lastSeenAt).filter(Boolean) as string[];
        return ts.length ? Math.max(...ts.map((t) => new Date(t).getTime())) : null;
      };

      const opTypesCount = (list: Integration[]) => new Set(list.flatMap((i) => i.activeOperationTypes ?? [])).size;

      const totalOps = (list: Integration[]) =>
        list.length > 0 ? list.reduce((s, i) => s + (i.totalOperations ?? i.totalEvents ?? 0), 0) : null;

      const currentLastSeenMs = latestMs(currentRange);
      const previousLastSeenMs = latestMs(previousRange);
      const now = Date.now();
      const previousAge = previousLastSeenMs != null ? now - previousLastSeenMs : null;
      const currentAge = currentLastSeenMs != null ? now - currentLastSeenMs : null;
      let lastSeenTrend = null;
      let lastSeenDelta = null;
      let lastSeenDeltaPositive = null;
      if (previousAge != null && currentAge == null) {
        lastSeenTrend = 'lower';
        lastSeenDeltaPositive = false;
        lastSeenDelta = 'None in range';
      } else if (previousAge != null && currentAge != null && previousAge > 0) {
        const diff = previousAge - currentAge;
        lastSeenTrend = diff > 0 ? 'higher' : diff < 0 ? 'lower' : 'same';
        lastSeenDeltaPositive = diff >= 0;
        const pct = (Math.abs(diff) / previousAge) * 100;
        if (pct >= 0.1) lastSeenDelta = `${pct.toFixed(1)}%`;
      }

      integrationStats.push(
        makeStat(
          'Success rate',
          avgSuccessRate(currentRange),
          avgSuccessRate(previousRange),
          (v) => `${v.toFixed(2)}%`,
        ),
        {
          label: 'Last seen',
          value: previousLastSeenMs != null ? formatRelativeTime(previousLastSeenMs) : '—',
          rangedValue: currentLastSeenMs != null ? formatRelativeTime(currentLastSeenMs) : '—',
          trend: lastSeenTrend,
          deltaDisplay: lastSeenDelta,
          deltaPositive: lastSeenDeltaPositive,
          deltaTooltip,
          rangeLabel: selectedTimeRange,
        },
        makeStat('Active ops', opTypesCount(currentRange), opTypesCount(previousRange), (v) => String(v)),
        makeStat('Total events', totalOps(currentRange), totalOps(previousRange), (v) => v.toLocaleString()),
      );
    }

    // Health-response-level cards (independent of per-install integrations array)
    return [
      ...integrationStats,
      makeStat(
        'Operations sent',
        this.args.currentRangeHealth?.operationsSent ?? null,
        this.args.previousRangeHealth?.operationsSent ?? null,
        (v) => v.toLocaleString(),
      ),
      makeStat(
        'Operations received',
        this.args.currentRangeHealth?.operationsReceived ?? null,
        this.args.previousRangeHealth?.operationsReceived ?? null,
        (v) => v.toLocaleString(),
      ),
      makeStat(
        'Access events',
        this.args.currentRangeHealth?.accessEventsReceived ?? null,
        this.args.previousRangeHealth?.accessEventsReceived ?? null,
        (v) => v.toLocaleString(),
      ),
    ];
  }

  <template>
    <div class="stats-cards-grid mb-8">
      {{#each this.stats as |stat|}}
        <UiCard class="p-4">
          <div class="text-xl font-bold">{{stat.rangedValue}}</div>
          <div class="text-xs text-carbon-50 mt-1">{{stat.label}}</div>
          {{#if stat.deltaDisplay}}
            <div class="flex items-center gap-1.5 mt-2">
              {{#if stat.deltaPositive}}
                <div
                  class="rounded-full bg-cilantro-5 w-5 h-5 flex items-center justify-center shrink-0 overflow-hidden"
                >
                  {{svgJar "carot-up" class="w-3 h-3 shrink-0 icon-fill-cilantro-50"}}
                </div>
                <span class="text-xs font-medium text-cilantro-50">{{stat.deltaDisplay}}</span>
              {{else}}
                <div class="rounded-full bg-red-40 w-5 h-5 flex items-center justify-center shrink-0 overflow-hidden">
                  {{svgJar "carot-down" class="w-3 h-3 shrink-0 icon-fill-white"}}
                </div>
                <span class="text-xs font-medium text-red-40">{{stat.deltaDisplay}}</span>
              {{/if}}
              <EmberTooltip @side="top" @text={{stat.deltaTooltip}} />
            </div>
          {{/if}}
        </UiCard>
      {{/each}}
    </div>
  </template>
}
